import { useState } from "react";
import { toast } from "react-toastify";
import { Send } from "lucide-react";
import { Button } from "../../components/ui/button";

import { ErrorBanner } from "../../features/web3/hyperliquid/components/ErrorBanner";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { FormDialog } from "../../features/web3/forex/components/FormDialog";

import { formatDateTime, formatUsd } from "../../lib/format";
import { usePermissions } from "../../hooks/usePermissions";
import { useBankedIncome, useReleaseBanked } from "./api";

const COLUMNS = [
  { label: "Held on" },
  { label: "User" },
  { label: "Amount", align: "right" },
  { label: "Why it was held" },
  { label: "", align: "right", sticky: true },
];

const displayName = (u) =>
  [u?.name, u?.surname].filter(Boolean).join(" ") || "—";

export default function HeldIncome() {
  const { can } = usePermissions();
  const list = useBankedIncome();
  const release = useReleaseBanked();

  const [confirming, setConfirming] = useState(null);

  const rows = list.data?.rows;
  const total = list.data?.total;
  const canRelease = can("POST:/api/admin/ib/banked/release");

  const run = async () => {
    try {
      await release.mutateAsync(confirming.incomeId);
      toast.success("Income released");
      setConfirming(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Release failed");
      setConfirming(null);
    }
  };

  return (
    <QueueLayout
      eyebrow="Forex IB"
      title="Held"
      accent="income"
      description={
        total != null
          ? `Forex income held back from NUs who went past $200 in a month without meeting that month's conditions. ${formatUsd(total)} is waiting.`
          : "Forex income held back from NUs who went past $200 in a month without meeting that month's conditions."
      }
      isFetching={list.isFetching}
      onRefresh={() => list.refetch()}
    >
      {list.error ? (
        <ErrorBanner error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <QueueTable
          columns={COLUMNS}
          isLoading={list.isLoading}
          rows={rows}
          renderRow={(row) => (
            <tr
              key={row.incomeId}
              className="transition-colors hover:bg-white/[0.02]"
            >
              <Td className="whitespace-nowrap font-mono text-muted-foreground">
                {formatDateTime(row.createdAt)}
              </Td>
              <Td>
                {/* `user` falls back to a bare { username } when the account is
                    gone, so the name can be missing while the username is not. */}
                <div className="font-medium">{displayName(row.user)}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {row.user?.username || "—"}
                </div>
              </Td>
              <Td align="right" className="whitespace-nowrap font-mono">
                {formatUsd(row.amount)}
              </Td>
              {/* The only record of why: the conditions it failed were measured
                  over a month that has since closed and cannot be recomputed. */}
              <Td className="text-muted-foreground">
                {row.bankedReason || "—"}
              </Td>
              <Td align="right" sticky>
                {canRelease && (
                  <Button
                    size="sm"
                    disabled={release.isPending}
                    onClick={() => setConfirming(row)}
                  >
                    <Send className="h-3.5 w-3.5" />
                    Release
                  </Button>
                )}
              </Td>
            </tr>
          )}
        />
      )}

      <FormDialog
        open={Boolean(confirming)}
        onOpenChange={(v) => !v && setConfirming(null)}
        title="Release held income"
        description="This pays straight into the wallet and cannot be undone."
        onSubmit={run}
        submitLabel="Release"
        loading={release.isPending}
        danger
      >
        <p className="text-sm text-muted-foreground">
          Paying{" "}
          <span className="font-mono text-foreground">
            {formatUsd(confirming?.amount)}
          </span>{" "}
          to{" "}
          <span className="font-mono text-foreground">
            {confirming?.user?.username || "—"}
          </span>
          .
        </p>
      </FormDialog>
    </QueueLayout>
  );
}
